import {Injectable} from '@angular/core';
import {catchError, first, forkJoin, of} from 'rxjs';
import {CountriesService} from './countries.service';
import {AirportsService} from './airports.service';
import {ListsService} from './lists.service';
import {TerminalsService} from './terminals.service';
import {SubfleetsService, Subfleet} from './subfleets.service';
import {TailsService} from './tails.service';
import {EquipmentClassService} from './equipment-class.service';
import {AmenityService} from './amenityService.service';
import { UaBase } from '../uas/ua.model';
import { CabinTargeting } from '../uas/editing/targeting/cabin-targeting.model';
import { TargetingRule } from '../uas/editing/targeting/targeting-rule.interface';
import {
  Airport,
  AirportList,
  Country,
  CountryList,
  EquipmentClass,
  EquipmentClassList,
  OriginAndDestinationList,
  SubfleetList,
  TailList,
  Terminal,
  TerminalList,
} from '../uas/editing/targeting/targeting-data.interface';
import { TailWithDisplayName } from './list-toggle.model';

@Injectable({providedIn: 'root'})
export class ListsToggleService {
  path: string = '/api';
  cabinTypes = [
    { code: 'Y', name: 'Economy' },
    { code: 'W', name: 'Premium Economy' },
    { code: 'C', name: 'Business' },
    { code: 'F', name: 'First' },
  ]
  constructor(
    private listsService: ListsService,
    private airportsService: AirportsService,
    private countriesService: CountriesService,
    private terminalsService: TerminalsService,
    private subfleetsService: SubfleetsService,
    private tailsService: TailsService,
    private equipmentClassService: EquipmentClassService,
    private amenityService: AmenityService,
  ) {}

  toggle(partner: any, rule: TargetingRule, ua: UaBase): void {
    // not sure if rule.option_type_id is correct paramter for getList
    let option_type = rule.option_type_id;
    // remove '_list'
    option_type = option_type.substring(0, option_type.length - 5);
    if (rule.operator_id === 'is-in' || rule.operator_id === 'is-not-in') {
      if (rule.option_type_id === 'stopover_connection_list') option_type = 'airport';
      this.listsService.getList(partner.id, option_type, rule.option_id).pipe(first()).subscribe((list: any) => {
        rule.rule_content = [];
        if (rule.option_type_id === 'airport_list' || rule.option_type_id === 'stopover_connection_list') {
          this.airportsService.getAirports(partner.id).pipe(first()).subscribe((all_airports: Airport[]) => {
            (list as AirportList).airport_codes.forEach((code) => {
              const airport = all_airports.find(a => a.code === code);
              rule.rule_content.push(airport);
            });
          });
        }
        if (rule.option_type_id === 'country_list') {
          this.countriesService.getCountries(partner.id).pipe(first()).subscribe((all_countries: Country[]) => {
            (list as CountryList).country_codes.forEach((code) => {
              const country = all_countries.find(c => c.code === code);
              rule.rule_content.push(country);
            });
          });
        }
        if (rule.option_type_id === 'terminal_list') {
          this.terminalsService.getTerminals(partner.id).pipe(first()).subscribe((all_terminals: Terminal[]) => {
            (list as TerminalList).terminal_codes.forEach((code) => {
              const terminal = all_terminals.find(t => t.code === code) ?? ({} as Terminal);
              terminal.name = terminal.airport_code + ' ' + (terminal?.name ?? '');
              rule.rule_content.push(terminal);
            });
          });
        }
        if (rule.option_type_id === 'subfleet_list') {
          this.subfleetsService.getSubfleets(partner.id).pipe(first()).subscribe((all_subfleets) => {
            return (list as SubfleetList).subfleet_ids.forEach(id => {
              const subfleet = all_subfleets.find(s => s.id === id);
              rule.rule_content.push(subfleet);
            });
          });
        }
        if (rule.option_type_id === 'tail_list') {
          forkJoin([
            this.subfleetsService.getSubfleets(partner.id),
            this.tailsService.getTails(partner.id),
          ]).subscribe((results: [Subfleet[], TailWithDisplayName[]]) => {
            const all_subfleets = results[0];
            const all_tails = results[1];
            (list as TailList).tail_ids.forEach(id => {
              const tail = all_tails.find(t => t.id === id);
              const subfleet = all_subfleets.find(s => s.id === tail?.subfleet_id);
              if (!tail || !subfleet) {
                // Referenced tail/subfleet no longer exists (e.g. deleted since the rule
                // was saved). Previously this crashed here (`tail.subfleet_id` read off
                // undefined); skip the stale id instead of adding a corrupt row.
                return;
              }
              tail.name = tail.number + ' ' + subfleet.name;
              rule.rule_content.push(tail);
            });
          });
        }
        if (rule.option_type_id === 'origin_and_destination_list') {
          forkJoin([
            this.airportsService.getAirports(partner.id).pipe(catchError(() => of([]))),
            this.countriesService.getCountries(partner.id).pipe(catchError(() => of([]))),
          ]).subscribe((results: [Airport[], Country[]]) => {
            const dataset = (list as OriginAndDestinationList).type === 'airport' ? results[0] : results[1];
            (list as OriginAndDestinationList).routes.forEach(route => {
              const from = dataset.find(d => d.code === route.from);
              const to = dataset.find(d => d.code === route.to);
              const both_ways = route.both_ways
                ? '<svg class="icon icon-arrow-right-arrow-left"><use xlink:href="#icon-arrow-right-arrow-left"></use></svg>'
                : '';
              rule.rule_content.push({
                name: `<div class="od-both-ways"><div class="od-rule-content-airport">${
                  from?.name
                }</div><div class="od-rule-content-airport"> to ${to?.name}</div>${both_ways}</div>`,
              });
            });
          });
        }
        if (rule.option_type_id === 'equipment_class_list') {
          (list as EquipmentClassList).equipment_class_values.forEach(equipmentClassValue => {
            rule.rule_content.push({name: equipmentClassValue});
          });
        }
      });
    }
    else if (rule.option_type_id === 'equipment_class') {
      this.equipmentClassService.getEquipmentClasses(partner.id).pipe(first()).subscribe((list: EquipmentClass[]) => {
        rule.rule_content = [];
        list.forEach(equipmentClass => {
          if (equipmentClass.name === rule.equipment_class_value) {
            equipmentClass.subfleets.forEach(subfleet => {
              rule.rule_content.push({name: subfleet.name})
            })
          }
        })
      });
    } else if ([
      'beverage',
      'entertainment',
      'food',
      'layout',
      'power',
      'seat',
      'wifi'
    ].includes(rule.option_type_id)) {
      let cabinTargetingCodes: string[] = [];
      this.cabinTypes.forEach(cabinType => {
        const cabin_names: string[] = [];
        const cabins = ['Economy', 'Premium Economy', 'Business', 'First Class'];
        ua.cabin_targeting.forEach((rule: CabinTargeting) => {
          cabin_names.push(cabins[rule.cabin_id -1] ?? '-');
        });
        if (cabin_names.includes(cabinType.name)) {
          cabinTargetingCodes.push(cabinType.code);
        }
      })
      this.amenityService.getAmenity(rule.option_type_id, 'en-US', partner, cabinTargetingCodes).pipe(first()).subscribe((response) => {
        rule.rule_content = [];
        response.forEach(amenity => {
          if (amenity.displayTextKey === rule.display_text_key) {
            amenity.subfleets.forEach(subfleet => {
              rule.rule_content.push({name: subfleet.name})
            });
          }
        })
      });
    }
  }
}
